import React from 'react';
import '../../styles/CallForParticipation.css';
import {
  Users, Landmark, Lightbulb, BarChart3, Handshake, Cog,
  Mic, MessageSquare, Presentation, Rocket,
  FileText, Download, ArrowRight, Mail,
} from 'lucide-react';
import data from '../../data/callForParticipation';

// Icon keys live in the data file so it stays free of JSX.
const AUDIENCE_ICONS = {
  executives: Users,
  policymakers: Landmark,
  technology: Lightbulb,
  investors: BarChart3,
  entrepreneurs: Handshake,
  innovators: Cog,
};

const FORMAT_ICONS = {
  spotlight: Mic,
  keynote: Users,
  panelist: Users,
  dialogue: MessageSquare,
  workshop: Presentation,
  pitch: Rocket,
};

export default function CallForParticipation() {
  const { contact } = data;

  return (
    <div className='cfp-page'>
      {/* Hero — the supplied banner already carries the title, tagline and the
          submission dates, so it stands on its own with no text overlay. */}
      <div className='cfp-banner'>
        <img
          src={data.bannerUrl}
          alt={`${data.eyebrow} — ${data.title}: ${data.tagline}. Submissions open ${data.submissionsOpen}, close ${data.submissionsClose}.`}
        />
      </div>

      {/* Intro */}
      <div className='cfp-intro'>
        <div className='cfp-eyebrow'>{data.eyebrow}</div>
        <h1 className='cfp-intro-title'>{data.tagline}</h1>
        {data.intro.map((paragraph, i) => (
          <p className='cfp-intro-text' key={i}>{paragraph}</p>
        ))}
      </div>

      {/* PDF download */}
      <div className='cfp-download-wrapper'>
        <div className='cfp-download'>
          <div className='cfp-download-icon'>
            <FileText />
          </div>
          <div className='cfp-download-body'>
            <div className='cfp-download-label'>Ready to learn more?</div>
            <div className='cfp-download-title'>Download the CANTO 2027 Call for Participation</div>
            <div className='cfp-download-text'>
              Discover the participation opportunities, priority themes, formats and submission requirements.
            </div>
          </div>
          <div className='cfp-download-action'>
            <a
              href={data.pdfUrl}
              target='_blank'
              rel='noopener noreferrer'
              className='cfp-download-btn'
            >
              Download PDF
              <Download size={18} />
            </a>
            <div className='cfp-download-meta'>PDF • CANTO Annual Conference 2027</div>
          </div>
        </div>
      </div>

      {/* Who should participate */}
      <div className='cfp-section'>
        <div className='cfp-section-title'>Who Should Participate?</div>
        <div className='cfp-audience-grid'>
          {data.audiences.map((audience) => {
            const Icon = AUDIENCE_ICONS[audience.icon];
            return (
              <div className='cfp-audience-card' key={audience.icon}>
                <div className='cfp-audience-icon'>{Icon && <Icon />}</div>
                <div className='cfp-audience-label'>{audience.label}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Twelve priority themes */}
      <div className='cfp-section'>
        <div className='cfp-section-title'>Twelve Priority Themes</div>
        <div className='cfp-themes-grid'>
          {data.themes.map((theme, i) => (
            <div className='cfp-theme-row' key={theme}>
              <span className='cfp-theme-number'>{i + 1}</span>
              <span className='cfp-theme-label'>{theme}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Participation formats */}
      <div className='cfp-section'>
        <div className='cfp-section-title'>Participation Formats</div>
        <div className='cfp-formats-grid'>
          {data.formats.map((format) => {
            const Icon = FORMAT_ICONS[format.icon];
            return (
              <div className='cfp-format-card' key={format.icon}>
                <div className='cfp-format-icon'>{Icon && <Icon />}</div>
                <div className='cfp-format-label'>{format.label}</div>
                <div className='cfp-format-duration'>{format.duration}</div>
                <div className='cfp-format-desc'>{format.desc}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Submit / contact footer band */}
      <div
        className='cfp-submit'
        style={{ backgroundImage: `url(${data.footerBgUrl})` }}
      >
        <div className='cfp-submit-inner'>
          <div className='cfp-submit-left'>
            <div className='cfp-submit-label'>Ready to Participate?</div>
            <div className='cfp-submit-title'>Submit Your Proposal</div>
            <div className='cfp-submit-sub'>Help shape the Caribbean's digital future.</div>
          </div>

          <a
            href={data.formUrl}
            target='_blank'
            rel='noopener noreferrer'
            className='cfp-submit-btn'
          >
            Submit Your Proposal
            <ArrowRight size={18} />
          </a>

          <div className='cfp-submit-contact'>
            <div className='cfp-submit-contact-label'>For more questions:</div>
            <div className='cfp-submit-contact-name'>{contact.name}</div>
            <div className='cfp-submit-contact-role'>{contact.role}</div>
            <a className='cfp-submit-contact-email' href={`mailto:${contact.email}`}>
              <Mail size={14} />
              {contact.email}
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
